import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { 
  Plus, Minus, ArrowLeft, ShoppingBag, X, Trash2, 
  Utensils, Hash, User, Phone, Search, ChevronRight, ChevronLeft, 
  Image as ImageIcon, AlertTriangle, CheckCircle2, Loader2, AlertCircle, Clock, Eye
} from 'lucide-react';

const TableOrder = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const tableFromUrl = searchParams.get('table');

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 1024;
  const isDesktop = windowWidth >= 1024;

  // UI States
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderStatus, setOrderStatus] = useState(null); 
  const [activeOrderData, setActiveOrderData] = useState(null); 
  
  // Custom Alert State
  const [customAlert, setCustomAlert] = useState({ show: false, message: '', type: 'error' });
  
  // Data States
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isTableValid, setIsTableValid] = useState(true);
  
  // Filter States
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  // Cart & Form States
  const [cart, setCart] = useState([]);
  const [formData, setFormData] = useState({ 
    name: '', 
    mobile: '', 
    table: tableFromUrl || '' 
  });
  
  const brandColor = "#ae7d54";

  // Helper for custom alerts
  const triggerAlert = (message, type = 'error') => {
    setCustomAlert({ show: true, message, type });
    setTimeout(() => setCustomAlert({ show: false, message: '', type: 'error' }), 4000);
  };

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    fetchInitialData();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // REAL-TIME STATUS POLLING - UPDATED TO USE /api/orders/live
  useEffect(() => {
    let interval;
    const fetchStatus = async () => {
      // Only poll if we have a table number and a reason to poll (Pending/Preparing)
      if (!formData.table || !orderStatus || orderStatus === 'Served') return;
      
      try {
        const res = await axios.get(`https://admin.theporch.in/api/orders/live`);
        if (res.data && Array.isArray(res.data)) {
          // Find the most recent order for the current table from the list
          const currentTableOrder = res.data.find(order => String(order.table_number) === String(formData.table));
          
          if (currentTableOrder) {
            if (currentTableOrder.status !== orderStatus) {
              setOrderStatus(currentTableOrder.status);
            }
            setActiveOrderData(currentTableOrder); 
          }
        }
      } catch (err) {
        console.error("Status poll error:", err);
      }
    };

    if (orderStatus && orderStatus !== 'Served') {
      fetchStatus(); // Call immediately
      interval = setInterval(fetchStatus, 5000); 
    }

    return () => clearInterval(interval);
  }, [orderStatus, formData.table]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [menuRes, tableRes, catRes] = await Promise.all([
        axios.get('https://admin.theporch.in/api/menu_items'),
        axios.post('https://admin.theporch.in/api/tables/fetch'),
        axios.get('https://admin.theporch.in/api/menu_categories')
      ]);

      const fetchedTables = tableRes.data;
      setMenuItems(menuRes.data);
      setTables(fetchedTables);
      setCategories(['All', ...catRes.data]);

      if (tableFromUrl) {
        const exists = fetchedTables.find(t => String(t.table_number) === String(tableFromUrl));
        if (!exists) {
          setIsTableValid(false);
        } else {
          setFormData(prev => ({ ...prev, table: tableFromUrl }));
        }
      } else {
        setIsTableValid(false); 
      }

    } catch (err) {
      console.error("Error fetching data:", err);
      triggerAlert("Failed to connect to server.");
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = useMemo(() => {
    return menuItems.filter(item => {
      const matchesCategory = selectedCategory === 'All' || item.category === selectedCategory;
      const matchesSearch = item.title.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [menuItems, selectedCategory, searchQuery]);

  const addToCart = (item) => {
    const exists = cart.find(x => x.id === item.id);
    if (exists) {
      setCart(cart.map(x => x.id === item.id ? { ...x, qty: x.qty + 1 } : x));
    } else {
      setCart([...cart, { id: item.id, display_name: item.title, display_price: item.price, qty: 1 }]);
    }
    if (isDesktop && !isCartOpen) setIsCartOpen(true);
  };

  const updateQty = (id, change) => {
    setCart(prevCart => {
      return prevCart.map(item => {
        if (item.id === id) {
          const newQty = item.qty + change;
          return { ...item, qty: newQty };
        }
        return item;
      }).filter(item => item.qty > 0);
    });
  };

  const removeFromCart = (id) => setCart(cart.filter(item => item.id !== id));
  const total = cart.reduce((acc, curr) => acc + (curr.display_price * curr.qty), 0);

  const submitOrder = async () => {
    if (!formData.table) return triggerAlert("Table number missing.");
    if (!formData.name.trim()) return triggerAlert("Guest Name is required.");
    const mob = formData.mobile.trim();
    if (mob && !/^[0-9]{10}$/.test(mob)) return triggerAlert("Please enter a valid 10-digit mobile number.");
    if (cart.length === 0) return triggerAlert("Your cart is empty!");

    setIsSubmitting(true);
    const payload = {
      table_number: formData.table,
      customer_name: formData.name.trim(),
      customer_mobile: mob,
      // Format items as a clean string for DB storage
      items: cart.map(i => `"${i.display_name} (x${i.qty})"`).join(', '),
      total_amount: total,
      status: 'Pending'
    };

    try {
      await axios.post('https://admin.theporch.in/api/orders', payload);
      setOrderStatus("Pending");
      setCart([]);
      setIsCartOpen(false);
      setShowSuccessModal(true);
    } catch (err) { 
      triggerAlert("Error saving order. Please contact staff."); 
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getItemInCart = (itemId) => cart.find(item => item.id === itemId);
  const getGridTemplate = () => {
    if (isMobile) return 'repeat(auto-fill, minmax(140px, 1fr))';
    if (isTablet) return 'repeat(auto-fill, minmax(180px, 1fr))';
    return isCartOpen ? 'repeat(auto-fill, minmax(200px, 1fr))' : 'repeat(auto-fill, minmax(220px, 1fr))';
  };

  if (!loading && !isTableValid) {
    return (
      <div style={{ ...styles.pageWrapper, justifyContent: 'center', alignItems: 'center', flexDirection: 'column', gap: '20px', padding: '20px' }}>
        <AlertTriangle size={64} color="#ef4444" />
        <h1 style={{ fontWeight: '800', color: '#1e293b' }}>Invalid Table</h1>
        <p style={{ color: '#64748b', textAlign: 'center' }}>Please scan the QR code on your table to start ordering.</p>
        <button onClick={() => navigate('/menu')} style={styles.activeCatBtn}>View Menu</button>
      </div>
    );
  }

  return (
    <div style={styles.pageWrapper}>
      <style>
        {`
          @keyframes slideInUp { from { transform: translateY(100%); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
          @keyframes slideInRight { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
          @keyframes scaleIn { from { transform: scale(0.9); opacity: 0; } to { transform: scale(1); opacity: 1; } }
          @keyframes pulse { 0% { box-shadow: 0 0 0 0 rgba(174, 125, 84, 0.7); } 70% { box-shadow: 0 0 0 10px rgba(174, 125, 84, 0); } 100% { box-shadow: 0 0 0 0 rgba(174, 125, 84, 0); } }
          .animate-scale-in { animation: scaleIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1); }
          .animate-slide-in { animation: slideInRight 0.4s ease-out; }
          .animate-slide-up { animation: slideInUp 0.3s ease-out forwards; }
          .custom-scroll::-webkit-scrollbar { width: 6px; height: 6px; }
          .custom-scroll::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }
          .category-nav::-webkit-scrollbar { display: none; }
          .food-image-container { height: ${isMobile ? '100px' : '150px'}; }
          .loader-spin { animation: spin 1s linear infinite; }
          @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        `}
      </style>

      {/* CUSTOM ALERT UI */}
      {customAlert.show && (
        <div className="animate-slide-in" style={{
          ...styles.alertToast,
          backgroundColor: customAlert.type === 'success' ? '#ecfdf5' : '#fef2f2',
          borderLeft: `5px solid ${customAlert.type === 'success' ? '#10b981' : '#ef4444'}`
        }}>
          {customAlert.type === 'success' ? <CheckCircle2 size={20} color="#10b981" /> : <AlertCircle size={20} color="#ef4444" />}
          <span style={{ color: '#1e293b', fontWeight: '600', fontSize: '14px' }}>{customAlert.message}</span>
          <X size={16} color="#94a3b8" onClick={() => setCustomAlert({ ...customAlert, show: false })} style={{ cursor: 'pointer', marginLeft: 'auto' }} />
        </div>
      )}

      {/* SUCCESS MODAL */}
      {showSuccessModal && (
        <div style={styles.modalOverlay}>
          <div className="animate-scale-in" style={{...styles.successCard, margin: isMobile ? '0 20px' : '0'}}>
            <div style={styles.iconCircle}>
              <CheckCircle2 size={40} color="white" />
            </div>
            <h2 style={{ fontSize: '24px', fontWeight: '800', color: '#1e293b', margin: '15px 0 10px', textAlign: 'center' }}>Order Placed!</h2>
            <p style={{ color: '#64748b', textAlign: 'center', lineHeight: '1.5', marginBottom: '25px' }}>
              Deliciousness is on the way! Your order for <strong>Table {formData.table}</strong> is being prepared.
            </p>
            <button onClick={() => setShowSuccessModal(false)} style={{ ...styles.checkoutBtn, background: brandColor }}>Order More Items</button>
          </div>
        </div>
      )}

      {/* VIEW ORDER DETAILS MODAL (MATCHING SCREENSHOT) */}
      {showOrderDetails && activeOrderData && (
        <div style={styles.modalOverlay}>
          <div className="animate-scale-in" style={{...styles.screenshotCard, margin: isMobile ? '0 20px' : '0'}}>
            {/* Header */}
            <div style={styles.ssHeader}>
              <div style={{fontSize: '12px', fontWeight: '700', letterSpacing: '1px', opacity: 0.8}}>ORDER DETAILS</div>
              <h2 style={{fontSize: '32px', fontWeight: '800', marginTop: '4px'}}>Table {formData.table}</h2>
              <button onClick={() => setShowOrderDetails(false)} style={styles.ssCloseBtn}><X size={24} color="white" /></button>
            </div>

            {/* Body */}
            <div style={styles.ssBody}>
              {/* Customer Info */}
              <div style={styles.ssCustomerBox}>
                 <div style={styles.ssIconCircle}><User size={24} color="white" /></div>
                 <div>
                    <div style={{fontSize: '11px', fontWeight: '800', color: '#94a3b8'}}>CUSTOMER</div>
                    <div style={{fontSize: '18px', fontWeight: '800', color: '#1e293b'}}>{activeOrderData.customer_name || 'Guest'}</div>
                 </div>
              </div>

              {/* Items Section */}
              <div style={{marginBottom: '10px'}}>
                <div style={{fontSize: '11px', fontWeight: '800', color: '#94a3b8', marginBottom: '12px'}}>ORDER ITEMS</div>
                {(activeOrderData.items || '').split(',').map((item, idx) => (
                  <div key={idx} style={styles.ssItemRow}>
                    <div style={styles.ssVegIndicator}><div style={styles.ssVegDot} /></div>
                    <div style={{fontSize: '16px', fontWeight: '700', color: '#334155'}}>{item.trim().replace(/"/g, '')}</div>
                  </div>
                ))}
              </div>

              {/* Status & Time Footer */}
              <div style={styles.ssMetaRow}>
                 <div>
                    <div style={{fontSize: '10px', fontWeight: '800', color: '#94a3b8'}}>PLACED AT</div>
                    <div style={{fontSize: '16px', fontWeight: '800', color: '#1e293b'}}>{formatTime(activeOrderData.created_at)}</div>
                 </div>
                 <div style={{textAlign: 'right'}}>
                    <div style={{fontSize: '10px', fontWeight: '800', color: '#94a3b8'}}>STATUS</div>
                    <div style={{fontSize: '16px', fontWeight: '800', color: '#ae7d54'}}>{activeOrderData.status}</div>
                 </div>
              </div>
            </div>

            {/* Total Footer */}
            <div style={styles.ssTotalFooter}>
               <span style={{fontSize: '18px', fontWeight: '700', color: '#64748b'}}>Total Amount</span>
               <span style={{fontSize: '32px', fontWeight: '900', color: '#1e293b'}}>₹{activeOrderData.total_amount || 0}.00</span>
            </div>
          </div>
        </div>
      )}

      <div style={styles.mainContentArea}>
        <section style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
          <div className="custom-scroll" style={{ padding: isMobile ? '12px' : '24px', flex: 1, overflowY: 'auto', paddingBottom: '140px' }}>
            <div style={{ ...styles.headerContainer, flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center' }}>
               <div style={{ display: 'flex', alignItems: 'center', gap: '12px', justifyContent: 'space-between' }}>
                  <h1 style={{ fontSize: isMobile ? '20px' : '24px', fontWeight: '800', color: '#1e293b' }}>Porch Cafe</h1>
                  <div style={styles.cartTrigger} onClick={() => setIsCartOpen(!isCartOpen)}>
                     <ShoppingBag size={22} color={brandColor} />
                     {cart.length > 0 && <span style={styles.badgeStyle}>{cart.length}</span>}
                  </div>
               </div>
               <div style={{ ...styles.searchWrapper, maxWidth: isMobile ? '100%' : '350px' }}>
                  <Search size={18} style={styles.searchIcon} />
                  <input style={styles.searchInput} placeholder="Search dishes..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
               </div>
            </div>

            <div className="category-nav" style={styles.categoryNav}>
              {categories.map(cat => (
                <button key={cat} onClick={() => setSelectedCategory(cat)} style={selectedCategory === cat ? styles.activeCatBtn : styles.inactiveCatBtn}>{cat}</button>
              ))}
            </div>

            {loading ? (
              <div style={styles.centerMsg}><Loader2 className="loader-spin" /> Loading Menu...</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: getGridTemplate(), gap: isMobile ? '12px' : '20px' }}>
                {filteredItems.map(item => {
                  const cartItem = getItemInCart(item.id);
                  const isInCart = !!cartItem;
                  return (
                    <div key={item.id} style={{ ...styles.foodCard, borderColor: isInCart ? brandColor : '#e2e8f0', backgroundColor: isInCart ? '#fefaf6' : 'white' }}>
                      <div className="food-image-container" style={styles.imageContainer}>
                        {item.image_url ? <img src={item.image_url} alt={item.title} style={styles.foodImage} /> : <ImageIcon size={30} color="#cbd5e1" />}
                      </div>
                      <div style={{ padding: isMobile ? '10px' : '15px' }}>
                        <div style={styles.foodTitle}>{item.title}</div>
                        <div style={styles.foodCategory}>{item.category}</div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px' }}>
                          <span style={styles.foodPrice}>₹{item.price}</span>
                          {isInCart ? (
                            <div style={styles.cardQtyToggle}>
                              <button onClick={() => updateQty(item.id, -1)} style={styles.cardQtyBtn}><Minus size={12} /></button>
                              <span style={styles.cardQtyValue}>{cartItem.qty}</span>
                              <button onClick={() => updateQty(item.id, 1)} style={styles.cardQtyBtn}><Plus size={12} /></button>
                            </div>
                          ) : (
                            <button onClick={() => addToCart(item)} style={styles.addIconCircle}><Plus size={14} color="white" /></button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        <div style={styles.bottomFixedSection}>
           {orderStatus && (
             <div className="animate-slide-up" style={styles.trackerBar} onClick={() => setShowOrderDetails(true)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={styles.pulseIcon}><Clock size={18} color="white" /></div>
                  <div>
                    <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: '700', letterSpacing: '0.5px' }}>LATEST ORDER</div>
                    <div style={{ fontSize: '14px', color: 'white', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '5px' }}>
                       Status: {orderStatus} <Eye size={14} color="#94a3b8" />
                    </div>
                  </div>
                </div>
                <button onClick={(e) => { e.stopPropagation(); setOrderStatus(null); }} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: 'white', borderRadius: '50%', padding: '5px', cursor: 'pointer' }}><X size={16}/></button>
             </div>
           )}

           {cart.length > 0 && !isCartOpen && (
             <div className="animate-slide-up" style={styles.floatingCartBar} onClick={() => setIsCartOpen(true)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                  <div style={styles.cartBarBadge}>{cart.length}</div>
                  <div>
                     <div style={{ fontSize: '15px', fontWeight: '800' }}>Review Order</div>
                     <div style={{ fontSize: '12px', opacity: 0.9 }}>{cart.length} items selected</div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                   <span style={{ fontSize: '20px', fontWeight: '900' }}>₹{total}</span>
                   <ChevronRight size={22} />
                </div>
             </div>
           )}
        </div>

        <aside style={{ 
          ...styles.cartAside,
          width: isCartOpen ? (isMobile ? '100%' : '380px') : '0px', 
          position: isMobile ? 'fixed' : 'relative',
          opacity: isCartOpen ? 1 : 0,
          visibility: isCartOpen ? 'visible' : 'hidden',
          zIndex: 1000
        }}>
          <div style={{ ...styles.cartContent, width: isMobile ? '100vw' : '380px' }}>
            <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ fontSize: '20px', fontWeight: '800', color: '#1e293b' }}>Checkout</h2>
              <button onClick={() => setIsCartOpen(false)} style={styles.closeCartBtn}><X size={20} /></button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
              <div style={styles.inputContainer}><Hash size={16} style={styles.inputIcon} /><input style={{ ...styles.inputWithIcon, backgroundColor: '#f1f5f9', cursor: 'not-allowed' }} value={`Table ${formData.table}`} readOnly /></div>
              <div style={styles.inputContainer}><User size={16} style={styles.inputIcon} /><input style={styles.inputWithIcon} placeholder="Guest Name * (Required)" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} /></div>
              <div style={styles.inputContainer}><Phone size={16} style={styles.inputIcon} /><input style={styles.inputWithIcon} type="tel" placeholder="Mobile No. (Optional)" value={formData.mobile} onChange={e => setFormData({ ...formData, mobile: e.target.value })} /></div>
            </div>

            <div className="custom-scroll" style={{ flex: 1, overflowY: 'auto' }}>
              {cart.length === 0 ? (
                <div style={styles.emptyCartStyle}><Utensils size={30} style={{ opacity: 0.2 }} /><p>Your cart is empty</p></div>
              ) : cart.map(item => (
                <div key={item.id} style={styles.cartItemRow}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '700', fontSize: '13px' }}>{item.display_name}</div>
                    <div style={{ color: brandColor, fontWeight: '700' }}>₹{item.display_price * item.qty}</div>
                  </div>
                  <div style={styles.qtyGroup}>
                    <button onClick={() => updateQty(item.id, -1)} style={styles.qtyBtn}><Minus size={12} /></button>
                    <span style={{ fontWeight: '800', width: '20px', textAlign: 'center' }}>{item.qty}</span>
                    <button onClick={() => updateQty(item.id, 1)} style={styles.qtyBtn}><Plus size={12} /></button>
                    <button onClick={() => removeFromCart(item.id)} style={styles.delBtn}><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
            </div>

            <div style={styles.cartFooter}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
                <span style={{ fontWeight: '600', color: '#64748b' }}>Grand Total</span>
                <span style={{ fontSize: '22px', fontWeight: '900' }}>₹{total}</span>
              </div>
              <button onClick={submitOrder} style={{ ...styles.checkoutBtn, opacity: isSubmitting ? 0.7 : 1 }} disabled={isSubmitting}>{isSubmitting ? 'Processing...' : 'Confirm Order'}</button>
            </div>
          </div>
        </aside>

        {isMobile && isCartOpen && <div style={styles.backdrop} onClick={() => setIsCartOpen(false)} />}
      </div>
    </div>
  );
};

const styles = {
  pageWrapper: { display: 'flex', height: '100%', width: '100%', background: '#f8fafc', overflow: 'hidden' },
  mainContentArea: { flex: 1, display: 'flex', height: '100%', width: '100%', overflow: 'hidden', position: 'relative' },
  alertToast: { position: 'fixed', top: '24px', right: '24px', padding: '16px 20px', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '12px', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)', zIndex: 3000, maxWidth: '320px', width: '90%' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(15, 23, 42, 0.7)', backdropFilter: 'blur(8px)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  successCard: { background: 'white', padding: '40px 30px', borderRadius: '24px', width: '100%', maxWidth: '400px', display: 'flex', flexDirection: 'column', alignItems: 'center', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' },
  screenshotCard: { background: 'white', borderRadius: '32px', width: '100%', maxWidth: '420px', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' },
  ssHeader: { background: '#1e293b', padding: '30px 24px', color: 'white', position: 'relative' },
  ssCloseBtn: { position: 'absolute', top: '24px', right: '24px', background: 'transparent', border: 'none', cursor: 'pointer' },
  ssBody: { padding: '24px' },
  ssCustomerBox: { background: '#f8fafc', padding: '16px', borderRadius: '20px', display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' },
  ssIconCircle: { background: '#ae7d54', width: '45px', height: '45px', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  ssItemRow: { background: '#f8fafc', padding: '16px', borderRadius: '18px', display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px', border: '1px solid #f1f5f9' },
  ssVegIndicator: { width: '18px', height: '18px', border: '1px solid #22c55e', borderRadius: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  ssVegDot: { width: '8px', height: '8px', background: '#22c55e', borderRadius: '50%' },
  ssMetaRow: { display: 'flex', justifyContent: 'space-between', padding: '20px 0', marginTop: '10px', borderTop: '1px dashed #e2e8f0' },
  ssTotalFooter: { background: '#ffffff', padding: '24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  iconCircle: { background: '#22c55e', width: '80px', height: '80px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '10px', boxShadow: '0 10px 15px rgba(34, 197, 94, 0.3)' },
  headerContainer: { marginBottom: '20px', display: 'flex', gap: '15px' },
  searchWrapper: { position: 'relative', flex: 1 },
  searchInput: { width: '100%', padding: '12px 15px 12px 45px', borderRadius: '12px', border: '1px solid #e2e8f0', outline: 'none' },
  searchIcon: { position: 'absolute', left: '15px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' },
  cartTrigger: { position: 'relative', cursor: 'pointer', background: 'white', padding: '10px', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', gap: '4px' },
  badgeStyle: { position: 'absolute', top: '-5px', right: '-5px', background: '#ef4444', color: 'white', fontSize: '10px', padding: '2px 6px', borderRadius: '50%', fontWeight: 'bold' },
  categoryNav: { display: 'flex', gap: '8px', marginBottom: '20px', overflowX: 'auto', paddingBottom: '8px' },
  inactiveCatBtn: { padding: '8px 18px', borderRadius: '10px', border: '1px solid #e2e8f0', background: 'white', cursor: 'pointer', whiteSpace: 'nowrap', fontSize: '14px', color: '#64748b' },
  activeCatBtn: { padding: '8px 18px', borderRadius: '10px', border: 'none', background: '#ae7d54', color: 'white', fontWeight: 'bold', cursor: 'pointer', whiteSpace: 'nowrap', fontSize: '14px', boxShadow: '0 4px 12px rgba(174, 125, 84, 0.25)' },
  foodCard: { background: 'white', borderRadius: '20px', border: '2px solid transparent', overflow: 'hidden', transition: 'all 0.3s ease', display: 'flex', flexDirection: 'column' },
  imageContainer: { background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' },
  foodImage: { width: '100%', height: '100%', objectFit: 'cover' },
  foodTitle: { fontWeight: '700', fontSize: '15px', color: '#1e293b' },
  foodCategory: { fontSize: '12px', color: '#94a3b8' },
  foodPrice: { color: '#ae7d54', fontWeight: '800', fontSize: '16px' },
  addIconCircle: { background: '#ae7d54', width: '28px', height: '28px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', cursor: 'pointer' },
  cardQtyToggle: { display: 'flex', alignItems: 'center', gap: '8px', background: '#ae7d54', padding: '4px 8px', borderRadius: '20px' },
  cardQtyBtn: { background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', borderRadius: '50%', width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  cardQtyValue: { color: 'white', fontWeight: 'bold', fontSize: '13px' },
  cartAside: { background: 'white', height: '100%', display: 'flex', flexDirection: 'column', transition: 'all 0.4s' },
  cartContent: { padding: '24px', display: 'flex', flexDirection: 'column', height: '100%', boxSizing: 'border-box' },
  inputContainer: { position: 'relative' },
  inputIcon: { position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' },
  inputWithIcon: { width: '100%', padding: '12px 10px 12px 40px', borderRadius: '12px', border: '1px solid #f1f5f9', background: '#f8fafc', fontSize: '14px', outline: 'none' },
  cartItemRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #f8fafc' },
  qtyGroup: { display: 'flex', alignItems: 'center', gap: '6px', background: '#f8fafc', padding: '6px', borderRadius: '10px' },
  qtyBtn: { background: 'white', border: '1px solid #e2e8f0', borderRadius: '6px', width: '24px', height: '24px', cursor: 'pointer' },
  delBtn: { color: '#f87171', marginLeft: '6px', background: 'none', border: 'none', cursor: 'pointer' },
  cartFooter: { borderTop: '2px dashed #f1f5f9', paddingTop: '20px', marginTop: 'auto' },
  checkoutBtn: { width: '100%', padding: '16px', borderRadius: '14px', border: 'none', background: '#1e293b', color: 'white', fontWeight: '800', cursor: 'pointer', fontSize: '16px' },
  backdrop: { position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(15, 23, 42, 0.5)', zIndex: 999, backdropFilter: 'blur(4px)' },
  centerMsg: { textAlign: 'center', padding: '40px', color: '#94a3b8', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' },
  emptyCartStyle: { textAlign: 'center', marginTop: '60px', color: '#cbd5e1', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '15px' },
  closeCartBtn: { background: '#f1f5f9', border: 'none', padding: '8px', borderRadius: '10px', cursor: 'pointer' },
  bottomFixedSection: { position: 'fixed', bottom: '24px', left: '50%', transform: 'translateX(-50%)', width: '100%', maxWidth: '500px', display: 'flex', flexDirection: 'column', gap: '12px', zIndex: 900, padding: '0 20px', boxSizing: 'border-box' },
  trackerBar: { background: '#1e293b', padding: '14px 20px', borderRadius: '18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', cursor: 'pointer' },
  floatingCartBar: { background: '#ae7d54', padding: '18px 24px', borderRadius: '22px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: 'white', boxShadow: '0 12px 30px rgba(174, 125, 84, 0.45)', cursor: 'pointer' },
  cartBarBadge: { background: 'white', color: '#ae7d54', width: '28px', height: '28px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '900', fontSize: '14px' },
  pulseIcon: { background: '#ae7d54', padding: '10px', borderRadius: '50%', animation: 'pulse 2s infinite' }
};

export default TableOrder;